import { Client, SimpleSchemaTypes } from '@datocms/cma-client-browser';
import { GraphQLClient } from 'graphql-request';
import ApiClient from '../apiClient/ApiClient';
import { TranscriptVoiceMapping } from '../transcript/types';

// Get all speakers for an album (showcase)
const ALBUM_SPEAKERS_QUERY = `query getAlbumSpeakers($albumId: ItemId) {
  album(filter: {id: {eq: $albumId}}) {
    id
    title
    speakers {
      id
      name
      title
      bio
      voice
    }
  }
}`;

// Get a story's album and its speakers
const STORY_SPEAKERS_QUERY = `query getStorySpeakers($storyId: ItemId) {
  story(filter: {id: {eq: $storyId}}) {
    id
    title
    speakers {
      id
      name
      title
      bio
      voice
    }
    primaryShowcase {
      id
      title
      speakers {
        id
        name
        title
        bio
        voice
      }
    }
  }
}`;

// Get speakers IDs for an album (showcase)
const ALBUM_SPEAKER_IDS_QUERY = `query getAlbumSpeakerIds($albumId: ItemId) {
  album(filter: {id: {eq: $albumId}}) {
    id
    speakers {
      id
    }
  }
}`;

// Get speaker IDs for a story
const STORY_SPEAKER_IDS_QUERY = `query getStorySpeakerIds($storyId: ItemId) {
  story(filter: {id: {eq: $storyId}}) {
    id
    speakers {
      id
    }
  }
}`;

export interface DatoSpeaker {
  id: string;
  name: string;
  title?: string;
  bio?: string;
  voice?: string;
}

interface AlbumSpeakersResponse {
  album: {
    id: string;
    title: string;
    speakers: DatoSpeaker[];
  };
}

interface StorySpeakersResponse {
  story: {
    id: string;
    title: string;
    speakers: DatoSpeaker[];
    primaryShowcase: {
      id: string;
      title: string;
      speakers: DatoSpeaker[];
    };
  };
}

interface AlbumSpeakerIdsResponse {
  album?: {
    id: string;
    speakers: { id: string }[];
  };
}

interface StorySpeakerIdsResponse {
  story?: {
    id: string;
    speakers: { id: string }[];
  };
}

/**
 * Repository for managing speakers and speaker mappings
 */
export class SpeakerRepository {
  private dClient: Client | ApiClient;
  private gqlClient: GraphQLClient;

  constructor(dClient: Client | ApiClient, gqlClient: GraphQLClient) {
    this.dClient = dClient;
    this.gqlClient = gqlClient;
  }

  /**
   * Get all speakers for an album
   */
  async getAlbumSpeakers(albumId: string): Promise<DatoSpeaker[]> {
    try {
      const response = await this.gqlClient.request<AlbumSpeakersResponse>(
        ALBUM_SPEAKERS_QUERY,
        { albumId }
      );
      
      return response.album.speakers || [];
    } catch (error) {
      console.error('Error fetching album speakers:', error);
      return [];
    }
  }

  /**
   * Get all speakers for a story's album
   */
  async getStorySpeakers(storyId: string): Promise<{ albumId: string; storySpeakers: DatoSpeaker[]; albumSpeakers: DatoSpeaker[] }> {
    try {
      const response = await this.gqlClient.request<StorySpeakersResponse>(
        STORY_SPEAKERS_QUERY,
        { storyId }
      );
      
      return {
        albumId: response.story.primaryShowcase.id,
        storySpeakers: response.story.speakers || [],
        albumSpeakers: response.story.primaryShowcase.speakers || []
      };
    } catch (error) {
      console.error('Error fetching story speakers:', error);
      return { albumId: '', storySpeakers: [], albumSpeakers: [] };
    }
  }

  /**
   * Create a new speaker in Dato CMS and add reference to the album
   */
  async createSpeaker(name: string, title?: string, bio?: string, voice?: string): Promise<DatoSpeaker | null> {
    try {
      const itemType = await this.dClient.itemTypes.find('speaker');

      const speaker = await this.dClient.items.create({
        item_type: { type: 'item_type', id: itemType.id },
        name,
        title,
        bio,
        voice,
      });

      return speaker as unknown as DatoSpeaker;

    } catch (error) {
      console.error('Error creating speaker:', error);
      return null;
    }
  }

  /**
   * Remove speaker reference from DatoCMS if it's no longer used in story
   * 
   * @param storyId - Story ID
   * @param datoSpeakerId - The DatoCMS speaker ID to potentially remove
   * @param currentMappings - Current speaker mappings to check if speaker is still used
   */
  async checkAndRemoveSpeakerReferenceIfUnused(
    storyId: string,
    datoSpeakerId: string,
    currentMappings: TranscriptVoiceMapping
  ): Promise<boolean> {
    try {
      // Check if the speaker is used anywhere else in the transcript
      const speakerStillUsed = Object.values(currentMappings.mappings).includes(datoSpeakerId);
      
      if (!speakerStillUsed) {
        // If the speaker is not used anywhere else, remove the reference from the story
        await this.removeSpeakerReferenceFromStory(datoSpeakerId, storyId);
      }
      
      return true;
    } catch (error) {
      console.error('Error checking speaker references:', error);
      return false;
    }
  }

  /**
   * Add a speaker reference to an album
   */
  async addSpeakerReferenceToAlbum(datoSpeakerId: string, albumId: string): Promise<void> {
    try {
      // Get the album to retrieve current speakers
      const album = await this.dClient.items.find(albumId) as SimpleSchemaTypes.Item & { speakers?: string[] };
      
      // Check if the speaker is already in the album
      const existingSpeakerIds = album.speakers || [];
      if (existingSpeakerIds.includes(datoSpeakerId)) {
        return; // Speaker already in album, no need to update
      }

      // Add the speaker to the album
      await this.dClient.items.update(albumId, {
        speakers: [...existingSpeakerIds, datoSpeakerId],
      });

      // Publish the album
      await this.dClient.items.publish(albumId);
    } catch (error) {
      console.error('Error adding speaker reference to album:', error);
      throw error;
    }
  }

  /**
   * Add a speaker reference to a story
   */
  async addSpeakerReferenceToStory(datoSpeakerId: string, storyId: string): Promise<void> {
    try {
      // Get the story to retrieve current speakers
      const story = await this.dClient.items.find(storyId) as SimpleSchemaTypes.Item & { speakers?: string[] };
      
      // Check if the speaker is already in the story
      const existingSpeakerIds = story.speakers || [];
      if (existingSpeakerIds.includes(datoSpeakerId)) {
        return; // Speaker already in story, no need to update
      }

      console.log('Adding speaker reference to story', storyId, datoSpeakerId);
      console.log('combined',  [...existingSpeakerIds, datoSpeakerId]);
      // Add the speaker to the story
      await this.dClient.items.update(storyId, {
        speakers: [...existingSpeakerIds, datoSpeakerId],
      });

      // Publish the story
      await this.dClient.items.publish(storyId);
    } catch (error) {
      console.error('Error adding speaker reference to story:', error);
      throw error;
    }
  }

  /**
   * Remove a speaker reference from a story
   * (but keep the speaker in the album for potential reuse)
   */
  async removeSpeakerReferenceFromStory(datoSpeakerId: string, storyId: string): Promise<void> {
    try {
      // Get the story to retrieve current speakers
      const story = await this.dClient.items.find(storyId) as SimpleSchemaTypes.Item & { speakers?: string[] };

      // Check if the speaker is in the story
      const existingSpeakerIds = story.speakers || [];
      if (!existingSpeakerIds.includes(datoSpeakerId)) {
        return; // Speaker not in story, no need to update
      }

      const newSpeakers = existingSpeakerIds.filter(id => id !== datoSpeakerId);
      // Remove the speaker from the story
      await this.dClient.items.update(storyId, {
        speakers: newSpeakers.length > 0 ? newSpeakers : null,
      });

      // Publish the story
      await this.dClient.items.publish(storyId);
    } catch (error) {
      console.error('Error removing speaker reference from story:', error);
      throw error;
    }
  }

  /**
   * Update an existing speaker in DatoCMS
   *
   * @param speakerId - The ID of the speaker to update
   * @param updates - Object containing properties to update: name, title, bio, and/or voice
   * @returns The updated speaker or null if update failed
   */
  async updateSpeaker(
    speakerId: string,
    updates: {
      name?: string;
      title?: string;
      bio?: string;
      voice?: string;
    }
  ): Promise<DatoSpeaker | null> {
    try {
      // Validate that we have at least one property to update
      if (!updates.name && !updates.title && !updates.bio && !updates.voice) {
        console.warn('No properties provided to update speaker');
        return null;
      }

      // Create update object with only the properties that are provided
      const updateData: Record<string, string | undefined> = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.bio !== undefined) updateData.bio = updates.bio;
      if (updates.voice !== undefined) updateData.voice = updates.voice;

      // Update the speaker in DatoCMS
      const updatedSpeaker = await this.dClient.items.update(speakerId, updateData);

      // Publish the speaker to make changes live
      await this.dClient.items.publish(speakerId);

      return updatedSpeaker as unknown as DatoSpeaker;
    } catch (error) {
      console.error('Error updating speaker:', error);
      return null;
    }
  }
}